import './ShareableMenu.css';

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { buildSeoUrl, seoConfig } from '../utils/seoConfig';

import FilterControls from '../components/FilterControls';
import FoodDetailModal from '../components/FoodDetailModal';
import Header from '../components/Header';
import MenuItemCard from '../components/MenuItemCard';
import { useSeo } from '../hooks/useSeo';
import { captureProductEvent, updateProductAnalyticsContext } from '../utils/productAnalytics';

function ShareableMenu() {
  // Get sessionId from URL params and navigation hook
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // State management - similar to HomePage but simplified
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFoodItem, setSelectedFoodItem] = useState(null);
  const [showFoodModal, setShowFoodModal] = useState(false);

  // Filtering and sorting state - reuse from HomePage
  const [sortBy, setSortBy] = useState('menu_order');
  const [filters, setFilters] = useState({
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    dairyFree: false,
  });
  const [allergenFilters, setAllergenFilters] = useState({
    cereals_containing_gluten: [],
    crustaceans: [],
    eggs: [],
    fish: [],
    peanuts: [],
    soybeans: [],
    milk: [],
    nuts: [],
    celery: [],
    mustard: [],
    sesame_seeds: [],
    sulphur_dioxide_and_sulphites: [],
    lupin: [],
    molluscs: [],
  });

  // Fetch shared menu data
  useEffect(() => {
    const fetchSharedMenu = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/share/${sessionId}`);

        if (!response.ok) {
          throw new Error('Failed to load shared menu');
        }

        const data = await response.json();
        setMenuData(data);
      } catch (err) {
        console.error('Error fetching shared menu:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      const sharedScanSessionId = new URLSearchParams(window.location.search).get('scanSessionId') || sessionId;
      updateProductAnalyticsContext({
        scanSessionId: sharedScanSessionId,
        source: 'shared_menu',
        entryPoint: 'shared_menu',
      });
      captureProductEvent('share-link-opened', { sessionId });
      fetchSharedMenu();
    }
  }, [sessionId]);

  // Filter and sorting functions - copied from HomePage
  const processMenuItems = items => {
    if (!Array.isArray(items)) return [];

    let filteredItems = [...items];

    // Apply dietary filters
    if (filters.vegetarian || filters.vegan || filters.glutenFree || filters.dairyFree) {
      filteredItems = filteredItems.filter(item => {
        return (
          (!filters.vegetarian || item.is_vegetarian === true) &&
          (!filters.vegan || item.is_vegan === true) &&
          (!filters.glutenFree || item.is_glutenFree === true) &&
          (!filters.dairyFree || item.is_dairyFree === true)
        );
      });
    }

    // Apply allergen filters
    const activeAllergenFilters = Object.entries(allergenFilters).filter(([_, statuses]) => statuses.length > 0);
    if (activeAllergenFilters.length > 0) {
      filteredItems = filteredItems.filter(item => {
        return activeAllergenFilters.every(([allergenKey, selectedStatuses]) => {
          const itemAllergenValue = item?.[allergenKey];

          if (!itemAllergenValue) {
            return true;
          }

          if (selectedStatuses.includes('high') && itemAllergenValue === 'high') {
            return false;
          }

          return true;
        });
      });
    }

    // Apply sorting
    filteredItems.sort((a, b) => {
      switch (sortBy) {
        case 'nutri_score':
          return (b.nutritional_score || 0) - (a.nutritional_score || 0);
        case 'calories_low':
          return (a.approximate_calories || 0) - (b.approximate_calories || 0);
        case 'calories_high':
          return (b.approximate_calories || 0) - (a.approximate_calories || 0);
        case 'price_low':
          return (a.price || 0) - (b.price || 0);
        case 'price_high':
          return (b.price || 0) - (a.price || 0);
        case 'name':
          const nameA = typeof a === 'string' ? a : a.menuResponse || '';
          const nameB = typeof b === 'string' ? b : b.menuResponse || '';
          return nameA.localeCompare(nameB);
        default:
          return 0;
      }
    });

    return filteredItems;
  };

  const toggleNutrientFilter = (allergenKey, status) => {
    setAllergenFilters(prev => ({
      ...prev,
      [allergenKey]: prev[allergenKey].includes(status)
        ? prev[allergenKey].filter(s => s !== status)
        : [...prev[allergenKey], status],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      vegetarian: false,
      vegan: false,
      glutenFree: false,
      dairyFree: false,
    });
    setAllergenFilters({
      cereals_containing_gluten: [],
      crustaceans: [],
      eggs: [],
      fish: [],
      peanuts: [],
      soybeans: [],
      milk: [],
      nuts: [],
      celery: [],
      mustard: [],
      sesame_seeds: [],
      sulphur_dioxide_and_sulphites: [],
      lupin: [],
      molluscs: [],
    });
  };

  // Modal functions
  const openFoodModal = (item, index) => {
    setSelectedFoodItem({ ...item, originalIndex: index });
    setShowFoodModal(true);
  };

  const closeFoodModal = () => {
    setShowFoodModal(false);
    setSelectedFoodItem(null);
  };

  // Navigation handlers
  const handleViewChange = view => {
    if (view === 'upload') {
      navigate('/');
    } else if (view === 'pricing') {
      navigate('/pricing');
    }
  };

  const itemCount = menuData?.menuItems?.length;
  const title = itemCount
    ? `Shared Menu with ${itemCount} Items | ${seoConfig.siteName}`
    : `Shared Menu | ${seoConfig.siteName}`;
  const description =
    '🍽️ Explore a shared menu with dish images plus nutrition and allergen highlights. Browse items fast and share the link with friends.';
  const canonical = sessionId ? buildSeoUrl(`/share/${sessionId}`) : buildSeoUrl('/share');
  const imageAlt = 'Shared menu preview';
  const shareImage = menuData?.s3MenuImageLink || seoConfig.defaultImage;

  useSeo({
    title,
    description,
    canonical,
    robots: 'index, follow',
    keywords: 'shared menu, menu images, menu nutrition, allergens, menu ai',
    og: {
      title,
      description,
      url: canonical,
      image: shareImage,
      imageAlt,
      type: 'website',
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      url: canonical,
      image: shareImage,
      imageAlt,
      site: seoConfig.twitterHandle,
      creator: seoConfig.twitterHandle,
      domain: seoConfig.twitterDomain,
    },
  });

  // Handle loading state
  if (loading) {
    return (
      <div className="shareable-menu-app">
        <Header
          isLoading={false}
          isAuthenticated={false}
          currentUser={null}
          onLogin={() => {}}
          onLogout={() => {}}
          currentView="upload"
          onViewChange={handleViewChange}
        />
        <main className="shareable-menu-content">
          <div className="shareable-menu-container">
            <div className="shareable-menu-hero">
              <h3 className="shareable-menu-title">Loading shared menu...</h3>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="shareable-menu-app">
        <Header
          isLoading={false}
          isAuthenticated={false}
          currentUser={null}
          onLogin={() => {}}
          onLogout={() => {}}
          currentView="upload"
          onViewChange={handleViewChange}
        />
        <main className="shareable-menu-content">
          <div className="shareable-menu-container">
            <div className="shareable-menu-hero">
              <h3 className="shareable-menu-title">Error loading shared menu</h3>
              <p className="shareable-menu-description">{error}</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const processedMenuItems = processMenuItems(menuData?.menuItems || []);

  return (
    <div className="shareable-menu-app">
      {/* Header Section */}
      <Header
        isLoading={false}
        isAuthenticated={false}
        currentUser={null}
        onLogin={() => (window.location.href = '/login')}
        onLogout={() => {}}
        currentView="upload"
        onViewChange={handleViewChange}
      />

      {/* Main Content */}
      <main className="shareable-menu-content">
        <div className="shareable-menu-container">
          {/* Hero Section */}
          <div className="shareable-menu-hero">
            <h3 className="shareable-menu-title">Shared Menu</h3>
            <p className="shareable-menu-description">
              Explore the menu with images, nutritional information 📊 and allergens risk ⚠️
            </p>
          </div>

          {/* Menu Image */}
          {menuData?.s3MenuImageLink && (
            <div className="shared-menu-image">
              <img src={menuData.s3MenuImageLink} alt="Menu" />
            </div>
          )}

          {/* Filter Controls */}
          {menuData?.menuItems && menuData.menuItems.length > 0 && (
            <FilterControls
              sortBy={sortBy}
              setSortBy={setSortBy}
              filters={filters}
              setFilters={setFilters}
              nutrientFilters={allergenFilters}
              toggleNutrientFilter={toggleNutrientFilter}
              clearAllFilters={clearAllFilters}
              userPlan="premium" // Show all features for shared menus
              onUpgradeClick={() => navigate('/pricing')}
            />
          )}

          {/* Items Count Display */}
          {menuData?.menuItems && menuData.menuItems.length > 0 && (
            <div className="shareable-menu-items-count">
              <p className="shareable-menu-items-count-text">
                {processedMenuItems.length} of {menuData.menuItems.length} items
              </p>
            </div>
          )}

          {/* Menu Items */}
          {menuData?.menuItems && menuData.menuItems.length > 0 && (
            <div className="shareable-menu-response">
              <div className="shareable-menu-success">
                <div className="shareable-menu-items-section">
                  <div className="shareable-menu-items-grid">
                    {processedMenuItems.map((item, index) => (
                      <MenuItemCard
                        key={`shared-item-${index}`}
                        item={item}
                        index={index}
                        onItemClick={openFoodModal}
                        onUpgradeClick={() => navigate('/pricing')}
                        userPlan="premium" // Show all features for shared menus
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Food Detail Modal */}
      <FoodDetailModal
        item={selectedFoodItem}
        isVisible={showFoodModal}
        onClose={closeFoodModal}
        onUpgradeClick={() => navigate('/pricing')}
        userPlan="premium" // Show all features for shared menus
      />

      <footer className="shareable-menu-footer">
        <p>
          Made with ❤️ by{' '}
          <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
            @jordibosch20
          </a>
        </p>
      </footer>
    </div>
  );
}

export default ShareableMenu;
